import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import backend from "../../../api";

/* ---------------------------- ROUTE MAP (edit me) ---------------------------- */
const ROUTE_MAP = {
  customer: {
    messages: (partnerId) => `/messages?userId=${partnerId}`,
    bookingList: () => `/appointments`,
    bookingDetail: (id) => `/bookings/`,
    invoiceList: () => `/invoices`,
    invoiceDetail: (id) => `/view-invoice/${id}`,
  },
  employee: {
    messages: (partnerId) => `/messages?userId=${partnerId}`,
    bookingList: () => `/appointments`,
    bookingDetail: (id) => `/view-appointment/details/${id}`,
    invoiceList: () => `/employee/invoices`,
    invoiceDetail: (id) => `/view-invoice/${id}`,
  },
  admin: {
    messages: (partnerId) => `/messages?userId=${partnerId}`,
    bookingList: () => `/appointments`,
    bookingDetail: (id) => `/view-appointment/details/${id}`,
    invoiceList: () => `/invoices`,
    invoiceDetail: (id) => `/view-invoice/${id}`,
  },
};

/* ---------------------------------- UI ---------------------------------- */
const BellIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" {...props} viewBox="0 0 448 512">
    <path d="M224 0c-17.7 0-32 14.3-32 32l0 19.2C119 66 64 130.6 64 208l0 18.8c0 47-17.3 92.4-48.5 127.6l-7.4 8.3c-8.4 9.4-10.4 22.9-5.3 34.4S19.4 416 32 416l384 0c12.6 0 24-7.4 29.2-18.9s3.1-25-5.3-34.4l-7.4-8.3C401.3 319.2 384 273.9 384 226.8l0-18.8c0-77.4-55-142-128-156.8L256 32c0-17.7-14.3-32-32-32zm45.3 493.3c12-12 18.7-28.3 18.7-45.3l-64 0-64 0c0 17 6.7 33.3 18.7 45.3s28.3 18.7 45.3 18.7s33.3-6.7 45.3-18.7z" />
  </svg>
);

export default function Notifications() {
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const company = useSelector((s) => s.companyInfo?.info);

  const role = String(user?.role || "").toLowerCase(); // "customer" | "employee" | "admin"
  const companyId = company?._id || user?.companyId;
  const routes = ROUTE_MAP[role] || ROUTE_MAP.customer;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [chat, setChat] = useState([]);         // unread chat items
  const [bookings, setBookings] = useState([]); // recent/assigned bookings
  const [invoices, setInvoices] = useState([]); // pending/overdue invoices

  const rootRef = useRef(null);
  function useMediaQuery(query) {
    const [matches, setMatches] = React.useState(() =>
      typeof window !== "undefined" ? window.matchMedia(query).matches : false
    );

    React.useEffect(() => {
      if (typeof window === "undefined") return;
      const mql = window.matchMedia(query);
      const onChange = (e) => setMatches(e.matches);
      mql.addEventListener?.("change", onChange) ?? mql.addListener(onChange);
      return () => mql.removeEventListener?.("change", onChange) ?? mql.removeListener(onChange);
    }, [query]);

    return matches;
  }
  const isNarrow = useMediaQuery("(max-width: 480px)");

  /* ------------------------------- Styles ------------------------------- */
  const S = {
    root: { position: "relative", display: "inline-block" },
    bellCon: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 38,
      height: 38,
      borderRadius: 999,
      background: "#ffffff",
      border: "1px solid #eaeaea",
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      cursor: "pointer",
      userSelect: "none",
    },
    badge: {
      position: "absolute",
      top: -4,
      right: -4,
      minWidth: 18,
      height: 18,
      padding: "0 5px",
      borderRadius: 9,
      background: "#df875b",
      color: "#fff",
      fontSize: 11,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 6px rgba(255,77,79,0.5)",
    },
    dropdown: {
      position: "absolute",
      right: 0,
      top: "calc(100% + 10px)",
      maxHeight: 560,
      overflowY: "auto",
      background: "#fff",
      border: "1px solid #eaeaea",
      boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
      borderRadius: 12,
      zIndex: 1000,
    },
    banner: {
      background: "var(--color-gradient)",
      color: "#fff",
      padding: "12px 16px",
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    bannerTitle: { margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: 0.5 },
    bannerChip: {
      background: "rgba(255,255,255,0.2)",
      color: "#fff",
      padding: "4px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
    },
    body: { padding: 12, background: "#fff" },
    sectionHeaderWrap: { margin: "8px 0 10px", display: "flex", alignItems: "center", justifyContent: "space-between" },
    sectionHeader: { margin: 0, fontSize: 12, letterSpacing: 0.6, textTransform: "uppercase", color: "#6b7280" },
    linkBtn: { fontSize: 12, fontWeight: '500', color: "#df875b", cursor: "pointer", background: "transparent", border: 0, padding: 0 },
    divider: { height: 1, background: "rgba(0,0,0,0.06)", marginTop: 6, borderRadius: 1, marginBottom: '7px' },
    item: { background: "#fafafa", border: "1px solid #eee", borderRadius: 10, padding: "10px 12px", marginBottom: 10, cursor: "pointer" },
    desc: { margin: 0, fontSize: 13.5, color: "#111827", lineHeight: 1.5 },
    date: { margin: "6px 0 0", fontSize: 12, color: "#6b7280" },
    pill: (c) => ({
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      color: c.text,
      background: c.bg,
      border: `1px solid ${c.border}`,
      marginLeft: 6,
      verticalAlign: "middle",
    }),
    emptyWrap: { textAlign: "center", padding: 24 },
    emptyText: { marginTop: 10, color: "#6b7280", fontSize: 13 },
  };

  /* ------------------------------- Helpers ------------------------------- */
  const statusColors = (label) => {
    const l = String(label || "").toLowerCase();
    if (l === "completed" || l === "paid") return { text: "#156c2f", bg: "rgba(36,161,72,0.10)", border: "#bdecc8" };
    if (l === "cancelled" || l === "canceled") return { text: "#7a1c1c", bg: "rgba(224,49,49,0.10)", border: "#f1b0b0" };
    if (l === "overdue") return { text: "#7a1c1c", bg: "rgba(224,49,49,0.10)", border: "#f1b0b0" };
    if (l === "on_the_way" || l === "on the way") return { text: "#155e75", bg: "rgba(125,211,252,0.2)", border: "#a5f3fc" };
    if (l === "delayed") return { text: "#895f14", bg: "rgba(250,176,5,0.10)", border: "#f4dda5" };
    return { text: "#895f14", bg: "rgba(250,176,5,0.10)", border: "#f4dda5" }; // pending/upcoming
  };
  const ellipsize = (s, n = 60) => (s?.length > n ? s.slice(0, n) + "…" : s || "");
  const parseStartsIn = (s) => {
    if (!s) return null;
    const [datePart, timePart = "00:00"] = String(s).split(" at ");
    const [y, m, d] = (datePart || "").split("-").map(Number);
    const [hh, mm] = (timePart || "").split(":").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
  };
  const fmtDateTime = (d) =>
    new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  const totalCount = useMemo(() => (chat?.length || 0) + (bookings?.length || 0) + (invoices?.length || 0), [chat, bookings, invoices]);

  /* ---------------------------- Outside click close ---------------------------- */
  useEffect(() => {
    const handler = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ------------------------------- Data loads ------------------------------- */
  useEffect(() => {
    let mounted = true;
    if (!user?._id) return;

    async function load() {
      setLoading(true);

      // 1) CHAT — unread last messages (use .seen like the chat pages)
      const chatPromise = (async () => {
        try {
          const { data } = await backend.get(`/api/chat/conversation?userId=${user._id}`);
          const items = (data?.data || [])
            .filter((c) => c.last && !c.last.seen && String(c.last.sender) !== String(user._id))
            .map((c) => ({
              kind: "chat",
              id: String(c._id),
              partnerId: String(c.partnerId || ""),
              partnerName: c.partnerName || "User",
              text: c.last?.text || "",
              createdAt: c.last?.createdAt || c.updatedAt || c.createdAt,
            }))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5);
          if (mounted) setChat(items);
        } catch {
          if (mounted) setChat([]);
        }
      })();

      // 2) BOOKINGS — role-based
      const bookingsPromise = (async () => {
        try {
          if (role === "customer") {
            const { data } = await backend.get(`/api/booking/by-user/${user._id}`);
            const list = (data?.data || [])
              .map((b) => {
                const when = parseStartsIn(b.startsIn);
                return {
                  kind: "booking",
                  id: String(b._id),
                  status: b?.status?.label || b?.status || "Upcoming",
                  date: when,
                  label: when ? `Your booking on ${when.toLocaleDateString()} at ${when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Your booking",
                  createdAt: when || Date.now(),
                };
              })
              .sort((a, b) => (b.date || 0) - (a.date || 0))
              .slice(0, 5);
            if (mounted) setBookings(list);
          } else {
            // admin / employee -> company list
            if (!companyId) {
              if (mounted) setBookings([]);
              return;
            }
            const { data } = await backend.get(`/api/bookings/bycompany?companyId=${companyId}&limit=200`);
            let items = (data?.items || []).map((b) => ({
              kind: "booking",
              id: String(b._id),
              customerName:
                [b?.contact?.firstName, b?.contact?.lastName].filter(Boolean).join(" ").trim() ||
                b?.userId?.name ||
                b?.userId?.email ||
                "Customer",
              dateLabel: b?.appointment?.date || "",
              timeLabel: b?.appointment?.timeSlot || "",
              status: b?.status || "upcoming",
              updatedAt: b?.updatedAt || b?.createdAt,
              teamMemberIds: (b?.teamMemberIds || []).map((m) => String(m?._id || m)),
            }));

            // employees only see their assigned bookings
            if (role === "employee") {
              const myId = String(user._id);
              items = items.filter((b) => b.teamMemberIds.includes(myId));
            }

            // pick most recent updates
            items = items
              .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
              .slice(0, 5)
              .map((b) => ({
                ...b,
                label: `Booking for ${b.customerName}`,
              }));

            if (mounted) setBookings(items);
          }
        } catch {
          if (mounted) setBookings([]);
        }
      })();

      // 3) INVOICES — pending/overdue only
      const invoicesPromise = (async () => {
        try {
          let url = `/api/invoices?limit=100`;
          if (role === "customer") url += `&userId=${user._id}`;
          else if (companyId) url += `&companyId=${companyId}`;
          const { data } = await backend.get(url);
          const items = (data?.items || [])
            .filter((inv) => ["pending", "overdue"].includes(String(inv.status || "").toLowerCase()))
            .map((inv) => ({
              kind: "invoice",
              id: String(inv._id),
              number: inv.invoiceNumber,
              status: inv.status,
              total: inv.total,
              issuedAt: inv.issuedAt || inv.createdAt,
            }))
            .sort((a, b) => new Date(b.issuedAt) - new Date(a.issuedAt))
            .slice(0, 5);
          if (mounted) setInvoices(items);
        } catch {
          if (mounted) setInvoices([]);
        }
      })();

      await Promise.all([chatPromise, bookingsPromise, invoicesPromise]);
      if (mounted) setLoading(false);
    }

    load();
    return () => {
      mounted = false;
    };
  }, [user?._id, companyId, role]);

  /* --------------------------------- UI bits --------------------------------- */
  const Section = ({ title, onAll, children, show }) =>
    !show ? null : (
      <>
        <div style={S.sectionHeaderWrap}>
          <h5 style={S.sectionHeader}>{title}</h5>
          {onAll ? (
            <button style={S.linkBtn} onClick={onAll}>
              View all
            </button>
          ) : null}
        </div>
        <div style={S.divider} />
        {children}
      </>
    );

  return (
    <div style={S.root} ref={rootRef}>
      <motion.div style={S.bellCon} onClick={() => setOpen((x) => !x)} whileTap={{ scale: 0.96 }}>
        <BellIcon height="20px" />
        <div style={S.badge}>{totalCount || 0}</div>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.18 }}
            style={{ ...S.dropdown, width: isNarrow ? "95vw" : 400 }}
          >
            <div style={S.banner}>
              <h5 style={S.bannerTitle}>Notifications</h5>
              <div style={S.bannerChip}>{totalCount} new</div>
            </div>

            <div style={S.body}>
              {loading ? (
                <div style={S.emptyWrap}>
                  <h5 style={{ margin: 0, color: "#969696" }}>Loading…</h5>
                </div>
              ) : totalCount === 0 ? (
                <div style={S.emptyWrap}>
                  <img src="/assets/images/empty-box.webp" alt="No notifications" style={{ height: "5rem", opacity: 0.8 }} />
                  <div style={S.emptyText}>No new notifications</div>
                </div>
              ) : (
                <>
                  {/* --------------------------- Messages --------------------------- */}
                  <Section
                    title="Messages"
                    show={chat.length > 0}
                    onAll={undefined /* add a route if you have a messages list page */}
                  >
                    {chat.map((c) => (
                      <div
                        key={`chat-${c.id}`}
                        style={S.item}
                        onClick={() => {
                          setOpen(false);
                          navigate(routes.messages(c.partnerId));
                        }}
                      >
                        <p style={S.desc}>
                          <strong>{c.partnerName}</strong> sent a new message: “{ellipsize(c.text)}”
                        </p>
                        <p style={S.date}>{fmtDateTime(c.createdAt)}</p>
                      </div>
                    ))}
                  </Section>

                  {/* --------------------------- Bookings --------------------------- */}
                  <Section
                    title="Bookings"
                    show={bookings.length > 0}
                    onAll={() => {
                      setOpen(false);
                      navigate(routes.bookingList());
                    }}
                  >
                    {bookings.map((b) => {
                      const label = b.label || "Booking";
                      const when =
                        b.date ||
                        (b.dateLabel
                          ? new Date(b.dateLabel)
                          : b.updatedAt || Date.now());
                      const line2 =
                        b.dateLabel || b.timeLabel
                          ? [b.dateLabel, b.timeLabel].filter(Boolean).join(" at ")
                          : null;
                      const c = statusColors(b.status);
                      return (
                        <div
                          key={`bk-${b.id}`}
                          style={S.item}
                          onClick={() => {
                            setOpen(false);
                            navigate(routes.bookingDetail(b.id));
                          }}
                        >
                          <p style={S.desc}>
                            <strong>{label}</strong>
                            <span style={S.pill(c)}>
                              {String(b.status || "").replace(/_/g, " ")}
                            </span>
                          </p>
                          <p style={S.date}>
                            {line2 ? line2 : fmtDateTime(when)}
                          </p>
                        </div>
                      );
                    })}
                  </Section>

                  {/* --------------------------- Invoices --------------------------- */}
                  <Section
                    title="Invoices"
                    show={invoices.length > 0}
                    onAll={() => {
                      setOpen(false);
                      navigate(routes.invoiceList());
                    }}
                  >
                    {invoices.map((inv) => {
                      const c = statusColors(inv.status);
                      return (
                        <div
                          key={`inv-${inv.id}`}
                          style={S.item}
                          onClick={() => {
                            setOpen(false);
                            navigate(routes.invoiceDetail(inv.id));
                          }}
                        >
                          <p style={S.desc}>
                            <strong>Invoice {inv.number || inv.id}</strong>
                            <span style={S.pill(c)}>{String(inv.status).toUpperCase()}</span>
                          </p>
                          <p style={S.date}>
                            {fmtDateTime(inv.issuedAt)} • Total: ${Number(inv.total || 0).toFixed(2)}
                          </p>
                        </div>
                      );
                    })}
                  </Section>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
